import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DropdownModule } from 'primeng/dropdown';
import {
  StudentSkillProgress,
  StudentProgressResponse,
  LicenseProgress,
  License,
  PRACTICE_RATING_LABELS,
  PRACTICE_RATING_EMOJIS,
  PRACTICE_RATING_COLORS
} from '../../../models/PracticeEvaluation';
import { PRACTICE_SKILL_CATEGORIES, PRACTICE_SKILL_CATEGORY_COLORS } from '../../../models/PracticeSkill';
import { PracticeEvaluationService } from '../../../services/practice-evaluation.service';

export interface SkillHistoryData {
  skill: { id: number; name: string; description: string; category: string };
  evaluations: any[];
}

interface LicenseOption {
  label: string;
  value: number | null;
}

@Component({
  selector: 'app-student-skills-progress',
  standalone: true,
  imports: [CommonModule, FormsModule, DropdownModule],
  templateUrl: './student-skills-progress.component.html',
  styleUrls: ['./student-skills-progress.component.css']
})
export class StudentSkillsProgressComponent implements OnInit {
  @Input() studentId!: number;
  @Output() openHistoryModal = new EventEmitter<SkillHistoryData>();

  progressData: StudentProgressResponse | null = null;
  isLoading = false;
  errorMessage: string | null = null;

  // Selector de carnet
  licenseOptions: LicenseOption[] = [];
  selectedLicenseId: number | null = null;

  // Referencias a constantes para el template
  categoryLabels = PRACTICE_SKILL_CATEGORIES;
  categoryColors = PRACTICE_SKILL_CATEGORY_COLORS;
  ratingLabels = PRACTICE_RATING_LABELS;
  ratingEmojis = PRACTICE_RATING_EMOJIS;
  ratingColors = PRACTICE_RATING_COLORS;

  constructor(private evaluationService: PracticeEvaluationService) {}

  ngOnInit(): void {
    if (this.studentId) {
      this.loadProgress();
    }
  }

  loadProgress(): void {
    this.isLoading = true;
    this.errorMessage = null;

    this.evaluationService.getStudentProgress(this.studentId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.progressData = response.data;
          this.buildLicenseOptions();
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading progress:', error);
        this.errorMessage = 'Error al cargar el progreso del alumno';
        this.isLoading = false;
      }
    });
  }

  buildLicenseOptions(): void {
    if (!this.progressData) return;

    this.licenseOptions = [];

    // Agregar cada carnet
    for (const licenseGroup of this.progressData.progress_by_license) {
      if (licenseGroup.license) {
        this.licenseOptions.push({
          label: licenseGroup.license.name,
          value: licenseGroup.license.id
        });
      }
    }

    // Seleccionar el primer carnet por defecto
    if (this.progressData.progress_by_license.length > 0 && this.progressData.progress_by_license[0].license) {
      this.selectedLicenseId = this.progressData.progress_by_license[0].license.id;
    }
  }

  getFilteredLicenseGroups(): LicenseProgress[] {
    if (!this.progressData) return [];

    return this.progressData.progress_by_license.filter(
      lg => lg.license?.id === this.selectedLicenseId
    );
  }

  getSelectedLicenseStats(): { total: number; conseguido: number; en_progreso: number; necesita_mejorar: number } {
    const stats = { total: 0, conseguido: 0, en_progreso: 0, necesita_mejorar: 0 };
    if (!this.progressData) return stats;

    const filteredGroups = this.getFilteredLicenseGroups();
    for (const group of filteredGroups) {
      for (const sp of group.skills_progress) {
        // Solo contar la última evaluación de cada habilidad
        if (sp.history_count > 0) {
          stats.total++;
          if (sp.latest_rating === 'conseguido') stats.conseguido++;
          else if (sp.latest_rating === 'en_progreso') stats.en_progreso++;
          else if (sp.latest_rating === 'necesita_mejorar') stats.necesita_mejorar++;
        }
      }
    }
    return stats;
  }

  onLicenseChange(): void {
    // El filtro y las estadísticas se actualizan automáticamente
  }

  getSkillsByCategory(category: string): StudentSkillProgress[] {
    if (!this.progressData) return [];
    return this.progressData.skills_progress.filter(sp => sp.skill.category === category);
  }

  getCategories(): string[] {
    if (!this.progressData) return [];
    const categories = new Set(this.progressData.skills_progress.map(sp => sp.skill.category));
    return Array.from(categories);
  }

  getCategoriesForLicense(licenseGroup: LicenseProgress): string[] {
    const categories = new Set(licenseGroup.skills_progress.map(sp => sp.skill.category));
    return Array.from(categories);
  }

  getSkillsByCategoryForLicense(licenseGroup: LicenseProgress, category: string): StudentSkillProgress[] {
    return licenseGroup.skills_progress.filter(sp => sp.skill.category === category);
  }

  getRatingPercentage(rating: string): number {
    const stats = this.getSelectedLicenseStats();
    if (stats.total === 0) return 0;

    const count = rating === 'conseguido'
      ? stats.conseguido
      : rating === 'en_progreso'
      ? stats.en_progreso
      : stats.necesita_mejorar;

    return Math.round((count / stats.total) * 100);
  }

  viewSkillHistory(skillId: number): void {
    this.evaluationService.getSkillHistory(this.studentId, skillId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.openHistoryModal.emit(response.data);
        }
      },
      error: (error) => {
        console.error('Error loading skill history:', error);
      }
    });
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getCategoryLabel(category: string): string {
    return this.categoryLabels[category as keyof typeof this.categoryLabels] || category;
  }

  getCategoryColor(category: string): string {
    return this.categoryColors[category as keyof typeof this.categoryColors] || '';
  }

  getRatingLabel(rating: string): string {
    return this.ratingLabels[rating as keyof typeof this.ratingLabels] || rating;
  }

  getRatingEmoji(rating: string): string {
    return this.ratingEmojis[rating as keyof typeof this.ratingEmojis] || '';
  }

  getRatingColor(rating: string): string {
    return this.ratingColors[rating as keyof typeof this.ratingColors] || '';
  }
}
